"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ResolvedSeoCountry } from "@/util/seoCountryPaths";

type ResolvedCountryContextValue = {
  country: ResolvedSeoCountry | null;
  setResolvedCountry: (country: ResolvedSeoCountry | null) => void;
};

const ResolvedCountryContext = createContext<ResolvedCountryContextValue>({
  country: null,
  setResolvedCountry: () => {},
});

/**
 * Holds the resolved SEO country (from URL slug, city/state mapping, or geo).
 */
export function ResolvedCountryProvider({ children }: { children: ReactNode }) {
  const [country, setCountry] = useState<ResolvedSeoCountry | null>(null);

  const setResolvedCountry = useCallback((next: ResolvedSeoCountry | null) => {
    setCountry(next);
  }, []);

  const value = useMemo(
    () => ({
      country,
      setResolvedCountry,
    }),
    [country, setResolvedCountry]
  );

  return (
    <ResolvedCountryContext.Provider value={value}>
      {children}
    </ResolvedCountryContext.Provider>
  );
}

export function useResolvedCountry(): ResolvedSeoCountry | null {
  return useContext(ResolvedCountryContext).country;
}

export function useSetResolvedCountry(): (
  country: ResolvedSeoCountry | null
) => void {
  return useContext(ResolvedCountryContext).setResolvedCountry;
}
